import { useMutation } from '@apollo/client/react';
import { TextField } from '@mui/material';
import { debounce } from 'lodash/fp';
import React, { useRef, useState } from 'react';

import OverwatchModal from '@/components/OverwatchModal';
import { CreateCompanyTagsDocument } from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

const normalDebounce = debounce(500);

interface CreateTagsModalProps {
  handleClose: () => void;
}

const DEFAULT_TAGS_DATA = {
  title: '',
};

const CreateTagsModal: React.FC<CreateTagsModalProps> = ({ handleClose }) => {
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [tag, setTag] = useState(DEFAULT_TAGS_DATA);
  const [createTags] = useMutation(CreateCompanyTagsDocument);

  const disableCreate = () => Object.values(tag).some((val) => val === '');

  const handleOnChange = (key: string, value: string) => {
    debounceFunc.cancel();
    debounceFunc(() => {
      setTag((prev) => ({
        ...prev,
        [key]: value,
      }));
    });
  };

  const handleSubmit = () => {
    createTags({
      variables: {
        companyTags: {
          title: [tag.title],
        },
      },
      onCompleted: () => {
        handleClose();
        displayNotification({
          type: 'success',
          text: 'Tag created successfully',
        });
      },
      onError: () => {
        displayNotification({
          type: 'error',
          text: 'Unable to create tag, try again later.',
        });
      },
      refetchQueries: ['getCompanyTags'],
    });
  };

  return (
    <OverwatchModal
      size="sm"
      title="Add a new tag"
      handleClose={handleClose}
      primaryButtonText="Create"
      primaryButtonAction={handleSubmit}
      disablePrimary={disableCreate()}
    >
      <TextField
        id="title"
        label="Title"
        required
        fullWidth
        autoFocus
        onChange={(e) => handleOnChange('title', e.target.value)}
      />
    </OverwatchModal>
  );
};

export default CreateTagsModal;
